/**
 * PATH: apps/qa/src/lib/offlineStore.ts
 * PURPOSE: Offline Store - IndexedDB-based persistence for QA Hub
 *
 * Features: Inspection caching, pending uploads queue,
 * background sync, conflict resolution
 *
 * DEPENDENCIES:
 *   - ./offline/types: Type definitions and constants
 *   - ./offline/syncOperations: Sync logic
 */

import { openDB, IDBPDatabase } from 'idb'
import type {
  PendingUpload, CachedInspection, CachedChecklistItem,
  SyncConflict, SyncResult, QAOfflineDB,
} from './offline/types'
import {
  DB_NAME, DB_VERSION, MAX_UPLOAD_ATTEMPTS, SYNC_INTERVAL_MS,
} from './offline/types'
import {
  processPendingUploads, syncDirtyInspections, syncDirtyChecklistItems,
} from './offline/syncOperations'
import { useAuthStore } from '@quotech/auth'

// Re-export types for consumers
export type { PendingUpload, CachedInspection, CachedChecklistItem, SyncConflict, SyncResult }

class OfflineStore {
  private db: IDBPDatabase<QAOfflineDB> | null = null
  private syncInterval: NodeJS.Timer | null = null
  private isOnline: boolean = navigator.onLine
  private onSyncCallbacks: ((result: SyncResult) => void)[] = []

  async init(): Promise<void> {
    if (this.db) return
    this.db = await openDB<QAOfflineDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        const inspStore = db.createObjectStore('inspections', { keyPath: 'id' })
        inspStore.createIndex('by-synced', 'syncedAt'); inspStore.createIndex('by-dirty', 'isDirty')
        const itemsStore = db.createObjectStore('checklistItems', { keyPath: 'id' })
        itemsStore.createIndex('by-inspection', 'inspectionId'); itemsStore.createIndex('by-area', 'areaId'); itemsStore.createIndex('by-dirty', 'isDirty')
        const uploadsStore = db.createObjectStore('pendingUploads', { keyPath: 'id' })
        uploadsStore.createIndex('by-entity', 'entityId'); uploadsStore.createIndex('by-created', 'createdAt'); uploadsStore.createIndex('by-attempts', 'attempts')
        const conflictsStore = db.createObjectStore('conflicts', { keyPath: 'id' })
        conflictsStore.createIndex('by-entity', 'entityId'); conflictsStore.createIndex('by-resolved', 'resolved')
        db.createObjectStore('metadata', { keyPath: 'key' })
      },
    })
    window.addEventListener('online', this.handleOnline.bind(this))
    window.addEventListener('offline', this.handleOffline.bind(this))
    if (this.isOnline) this.startBackgroundSync()
  }

  private handleOnline(): void { this.isOnline = true; this.startBackgroundSync(); this.syncAll() }
  private handleOffline(): void { this.isOnline = false; this.stopBackgroundSync() }
  private startBackgroundSync(): void { if (this.syncInterval) return; this.syncInterval = setInterval(() => { if (this.isOnline) this.syncAll().catch(e => console.warn('[OfflineStore] Background sync error:', e)) }, SYNC_INTERVAL_MS) }
  private stopBackgroundSync(): void { if (this.syncInterval) { clearInterval(this.syncInterval); this.syncInterval = null } }

  // ── Inspection Operations ───────────────────────────────────────
  async cacheInspection(id: string, data: Record<string, unknown>): Promise<void> {
    if (!this.db) await this.init()
    const existing = await this.db!.get('inspections', id)
    await this.db!.put('inspections', { id, data, syncedAt: new Date(), modifiedAt: existing?.modifiedAt || new Date(), isDirty: existing?.isDirty || false })
  }
  async getInspection(id: string): Promise<CachedInspection | undefined> { if (!this.db) await this.init(); return this.db!.get('inspections', id) }
  async updateInspection(id: string, data: Record<string, unknown>): Promise<void> {
    if (!this.db) await this.init()
    const existing = await this.db!.get('inspections', id)
    if (!existing) { await this.cacheInspection(id, data); return }
    await this.db!.put('inspections', { ...existing, data: { ...existing.data, ...data }, modifiedAt: new Date(), isDirty: true })
  }
  async getDirtyInspections(): Promise<CachedInspection[]> {
    if (!this.db) await this.init()
    // Note: booleans are not valid IDB keys, so we filter in memory instead of using the index
    const all = await this.db!.getAll('inspections')
    return all.filter(i => i.isDirty)
  }

  // ── Checklist Item Operations ───────────────────────────────────
  async cacheChecklistItem(item: CachedChecklistItem): Promise<void> { if (!this.db) await this.init(); await this.db!.put('checklistItems', item) }
  async getChecklistItem(id: string): Promise<CachedChecklistItem | undefined> { if (!this.db) await this.init(); return this.db!.get('checklistItems', id) }
  async getItemsByInspection(inspectionId: string): Promise<CachedChecklistItem[]> { if (!this.db) await this.init(); return this.db!.getAllFromIndex('checklistItems', 'by-inspection', inspectionId) }
  async getItemsByArea(areaId: string): Promise<CachedChecklistItem[]> { if (!this.db) await this.init(); return this.db!.getAllFromIndex('checklistItems', 'by-area', areaId) }
  async updateChecklistItem(id: string, updates: Partial<CachedChecklistItem>): Promise<void> {
    if (!this.db) await this.init()
    const existing = await this.db!.get('checklistItems', id); if (!existing) return
    await this.db!.put('checklistItems', { ...existing, ...updates, modifiedAt: new Date(), isDirty: true })
  }
  async getDirtyChecklistItems(): Promise<CachedChecklistItem[]> {
    if (!this.db) await this.init()
    const all = await this.db!.getAll('checklistItems')
    return all.filter(i => i.isDirty)
  }

  // ── Upload Operations ───────────────────────────────────────────
  async queueUpload(upload: Omit<PendingUpload, 'id' | 'createdAt' | 'attempts'>): Promise<string> {
    if (!this.db) await this.init()
    const auth = useAuthStore.getState()
    const id = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    await this.db!.put('pendingUploads', {
      ...upload,
      id,
      createdAt: new Date(),
      attempts: 0,
      ownerUserId: upload.ownerUserId ?? (auth.user?.id != null ? String(auth.user.id) : undefined),
      ownerOrganisationId:
        upload.ownerOrganisationId
        ?? (auth.organisationId != null ? String(auth.organisationId) : undefined),
    })
    if (this.isOnline) processPendingUploads(this.db!, MAX_UPLOAD_ATTEMPTS)
    return id
  }
  async getPendingUploads(): Promise<PendingUpload[]> { if (!this.db) await this.init(); return this.db!.getAll('pendingUploads') }
  async getPendingUploadCount(): Promise<number> { if (!this.db) await this.init(); return (await this.db!.getAll('pendingUploads')).length }
  async removePendingUpload(id: string): Promise<void> { if (!this.db) await this.init(); await this.db!.delete('pendingUploads', id) }

  // ── Conflict Operations ─────────────────────────────────────────
  async addConflict(conflict: Omit<SyncConflict, 'id' | 'conflictedAt' | 'resolved'>): Promise<string> {
    if (!this.db) await this.init()
    const id = `conflict-${Date.now()}`
    await this.db!.put('conflicts', { ...conflict, id, conflictedAt: new Date(), resolved: false })
    return id
  }
  async getUnresolvedConflicts(): Promise<SyncConflict[]> {
    if (!this.db) await this.init()
    const all = await this.db!.getAll('conflicts')
    return all.filter(c => !c.resolved)
  }
  async resolveConflict(id: string): Promise<void> {
    if (!this.db) await this.init()
    const conflict = await this.db!.get('conflicts', id); if (!conflict) return
    conflict.resolved = true; await this.db!.put('conflicts', conflict)
  }

  // ── Sync ────────────────────────────────────────────────────────
  async syncAll(): Promise<SyncResult> {
    if (!this.isOnline) return { success: false, message: 'Offline', synced: 0, failed: 0, conflicts: 0 }
    if (!this.db) await this.init()
    const uploadResult = await processPendingUploads(this.db!, MAX_UPLOAD_ATTEMPTS)
    const inspResult = await syncDirtyInspections(this.db!, this.addConflict.bind(this))
    const itemResult = await syncDirtyChecklistItems(this.db!)
    const result: SyncResult = {
      success: uploadResult.failed + inspResult.failed + itemResult.failed === 0,
      message: 'Sync complete',
      synced: uploadResult.synced + inspResult.synced + itemResult.synced,
      failed: uploadResult.failed + inspResult.failed + itemResult.failed,
      conflicts: inspResult.conflicts + itemResult.conflicts,
    }
    this.onSyncCallbacks.forEach(cb => cb(result))
    return result
  }

  // ── Metadata & Utilities ────────────────────────────────────────
  async setMetadata(key: string, value: unknown): Promise<void> { if (!this.db) await this.init(); await this.db!.put('metadata', { key, value, updatedAt: new Date() }) }
  async getMetadata(key: string): Promise<unknown> { if (!this.db) await this.init(); const e = await this.db!.get('metadata', key); return e?.value }
  onSync(callback: (result: SyncResult) => void): () => void { this.onSyncCallbacks.push(callback); return () => { this.onSyncCallbacks = this.onSyncCallbacks.filter(cb => cb !== callback) } }
  async clear(): Promise<void> {
    if (!this.db) await this.init()
    const tx = this.db!.transaction(['inspections', 'checklistItems', 'pendingUploads', 'conflicts', 'metadata'], 'readwrite')
    await Promise.all([tx.objectStore('inspections').clear(), tx.objectStore('checklistItems').clear(), tx.objectStore('pendingUploads').clear(), tx.objectStore('conflicts').clear(), tx.objectStore('metadata').clear()])
  }
  async getStorageUsage(): Promise<{ used: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) { const e = await navigator.storage.estimate(); return { used: e.usage || 0, quota: e.quota || 0 } }
    return { used: 0, quota: 0 }
  }
  get online(): boolean { return this.isOnline }
}

export const offlineStore = new OfflineStore()
offlineStore.init().catch(console.error)
export default offlineStore
